import { DeleteOutlined, LoadingOutlined } from '@ant-design/icons';
import { ProductDoc, ProductInventoryDoc, ProductInventoryUnit, ProductStateCode } from '@gooduncles/gu-app-schema';
import { Alert, Button, Modal, Popover, Tag, notification } from 'antd';
import { ColDef, GridApi, GridReadyEvent, SelectionChangedEvent, ValueParserParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { FC, useEffect, useRef, useState } from 'react';
import { productStates } from 'src/constants';

import { AuditLogger, FieldChange } from 'src/utils/audit-logger';
import { errorObjectToString } from 'src/utils/common';
import { FirebaseManager } from 'src/utils/firebase/firebase-manager';
import * as XLSX from 'xlsx';
import {
  getProductInventories,
  getProductsWithWhere,
  updateInventoryUnitCost,
  updateProduct,
} from 'src/utils/firebase/firebase-short-cut';

import FileUploadArea from 'src/components/Common/FileUploadArea/FileUploadArea';

const SUPPLIER_ID = 'fugBvrUFgCg2fOJOMeAN';

const firebaseManager = FirebaseManager.getInstance();
const auditLogger = AuditLogger.getInstance();

interface WoojooExcelRow {
  상품코드?: string;
  '상품명(매장)'?: string;
  품절여부?: string;
  '금일 공급단가'?: string;
}

interface DiffRow {
  productId: string;
  code: string;
  inventoryName: string;
  fullName: string;
  currentState: ProductStateCode;
  targetState: ProductStateCode;
  /** 공급가 반영 대상: 재고 id + 단위 (매입가 정본은 재고 uom.cost) */
  inventoryId: string;
  inventoryUnit: ProductInventoryUnit;
  /** 현재 매입원가 */
  currentCost: number;
  /** 엑셀 금일 공급단가(반올림). 값 없으면 null */
  excelCost: number | null;
  /** 담당자 직접 입력 공급가. 입력 시 엑셀값 대신 반영 */
  overrideCost: number | null;
  /** 한 재고에 개별/박스 등 여러 단위가 물린 복합 단위 재고 여부 */
  multiUnit: boolean;
}

/** '₩21,800.00' → 21800 (반올림). 파싱 불가 시 null */
const parseWon = (value: string | undefined): number | null => {
  if (value == null) return null;
  const n = parseFloat(value.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? Math.round(n) : null;
};

const wonFormatter = (params: { value: number | null | undefined }) =>
  params.value == null ? '' : Number(params.value).toLocaleString();

/**
 * 엑셀을 object 배열로 파싱한다.
 * 공유 readExcelFile은 후행 빈 열(예: I1)을 만나면 던지므로, 여기선 sheet_to_json을 직접 쓴다.
 */
const parseExcel = (file: File): Promise<WoojooExcelRow[]> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workBook = XLSX.read(e.target?.result, { type: 'binary' });
        const workSheet = workBook.Sheets[workBook.SheetNames[0]];
        resolve(XLSX.utils.sheet_to_json<WoojooExcelRow>(workSheet, { raw: false, blankrows: false }));
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsBinaryString(file);
  });

/** 엑셀 '품절여부' → 상품 상태. 판매중/품절만 취급한다. */
const soldOutToState = (value: string | undefined): ProductStateCode | null => {
  const v = value?.trim();
  if (v === '판매중') return ProductStateCode.STOCK;
  if (v === '품절') return ProductStateCode.OUTOFSTOCK;
  return null;
};

const previewColDefs: ColDef<WoojooExcelRow>[] = [
  { headerName: '상품코드', field: '상품코드', width: 120 },
  { headerName: '상품명(매장)', field: '상품명(매장)', flex: 1 },
  { headerName: '품절여부', field: '품절여부', width: 120 },
  {
    headerName: '금일 공급단가',
    field: '금일 공급단가',
    width: 140,
    // 소수점 없이 반올림 정수로 표기
    valueFormatter: (params) => {
      const n = parseWon(params.value);
      return n == null ? params.value ?? '' : `₩${n.toLocaleString()}`;
    },
  },
];

const stateFormatter = (params: { value: ProductStateCode }) => productStates[params.value] ?? params.value;

const unitLabel: Record<string, string> = {
  [ProductInventoryUnit.EA]: '낱개(EA)',
  [ProductInventoryUnit.BUNDLE]: '묶음(BUNDLE)',
};

/** 단위 표시. 복합 단위(개별+박스) 재고면 '복합' 태그를 함께 노출한다. */
const UnitCell: FC = (params: any) => {
  const unit: string = params.data?.inventoryUnit;
  return (
    <span>
      {unitLabel[unit] ?? unit}
      {params.data?.multiUnit && (
        <Tag color='purple' style={{ marginLeft: 6 }}>
          복합
        </Tag>
      )}
    </span>
  );
};

/** 엑셀 공급가를 현재 매입원가와 비교해 증감(인상 빨강/인하 파랑)을 색상으로 표시한다. */
const ExcelCostCell: FC = (params: any) => {
  const excel: number | null = params.data?.excelCost ?? null;
  const current: number = params.data?.currentCost ?? 0;
  if (excel == null) return null;
  const diff = excel - current;
  const color = diff > 0 ? '#cf1322' : diff < 0 ? '#1677ff' : '#8c8c8c';
  return (
    <span style={{ color }}>
      ₩{excel.toLocaleString()}
      {diff !== 0 && (
        <span style={{ marginLeft: 4, fontSize: 12 }}>
          ({diff > 0 ? '+' : ''}
          {diff.toLocaleString()})
        </span>
      )}
    </span>
  );
};

const diffColDefs: ColDef<DiffRow>[] = [
  { headerCheckboxSelection: true, checkboxSelection: true, width: 50, pinned: 'left' },
  { headerName: '상품코드', field: 'code', width: 110 },
  { headerName: '재고명', field: 'inventoryName', width: 200 },
  { headerName: '상품명', field: 'fullName', flex: 1 },
  { headerName: '단위', field: 'inventoryUnit', width: 150, cellRenderer: UnitCell },
  { headerName: '현재 상태', field: 'currentState', width: 110, valueFormatter: stateFormatter },
  { headerName: '반영 상태', field: 'targetState', width: 110, valueFormatter: stateFormatter },
  { headerName: '현재 공급가', field: 'currentCost', width: 110, valueFormatter: wonFormatter, type: 'rightAligned' },
  { headerName: '엑셀 공급가', field: 'excelCost', width: 160, cellRenderer: ExcelCostCell, type: 'rightAligned' },
  {
    headerName: '직접 입력 공급가',
    field: 'overrideCost',
    width: 140,
    editable: true,
    type: 'rightAligned',
    valueFormatter: wonFormatter,
    // 빈칸이면 엑셀 공급가를 반영, 값 입력 시 그 값을 우선 반영한다.
    valueParser: (params: ValueParserParams<DiffRow>) => {
      const n = parseFloat(String(params.newValue).replace(/[^0-9.-]/g, ''));
      return Number.isFinite(n) ? Math.round(n) : null;
    },
    cellStyle: { backgroundColor: '#fffbe6' },
  },
];

interface SyncResult {
  diffs: DiffRow[];
  /** 엑셀상 상태가 달라 반영 대상이지만 판매중지라 제외된 상품 목록 */
  discontinued: DiffRow[];
}

/** 엑셀 데이터를 서버 재고/상품과 비교해 변경이 필요한 상품만 추린다. */
const computeDiffs = async (excelRows: WoojooExcelRow[]): Promise<SyncResult> => {
  const codeToTarget = new Map<string, ProductStateCode>();
  const codeToCost = new Map<string, number>();
  excelRows.forEach((row) => {
    const code = row.상품코드?.toString().trim();
    if (!code) return;
    const target = soldOutToState(row.품절여부);
    if (target) codeToTarget.set(code, target);
    const cost = parseWon(row['금일 공급단가']);
    if (cost != null) codeToCost.set(code, cost);
  });

  const [inventories, products] = await Promise.all([
    getProductInventories([
      ['purchaseConfig.supplierId', '==', SUPPLIER_ID],
      ['active', '==', true],
    ]) as Promise<ProductInventoryDoc[]>,
    getProductsWithWhere([['supplierId', '==', SUPPLIER_ID]]) as Promise<ProductDoc[]>,
  ]);

  const diffs: DiffRow[] = [];
  const discontinued: DiffRow[] = [];

  inventories.forEach((inventory) => {
    const code = inventory.purchaseConfig.purchaseProductName?.toString().trim();
    if (!code) return;
    const targetStateRaw = codeToTarget.get(code);
    const excelCost = codeToCost.get(code) ?? null;
    // 엑셀에 상태/공급가 어느 것도 없으면 비교할 것이 없다.
    if (targetStateRaw === undefined && excelCost == null) return;

    // unit별로 상태/공급가 차이를 계산한다. (한 재고에 EA/BUNDLE 등 여러 unit이 물릴 수 있음)
    const evaluated = products
      .filter((product) => product.inventoryId === inventory._id)
      .map((product) => {
        // 품절여부가 없으면 상태는 그대로 둔다.
        const targetState = targetStateRaw ?? product.state;
        // 공급가는 재고 uom[상품단위].cost가 정본. 엑셀 공급단가는 unit별로 값이 달라야 하므로
        // 담당자가 unit을 보고 직접 판단하도록 값만 노출한다. 해당 단위가 재고에 없으면 cost 반영 불가.
        const unit = product.inventoryUnit;
        const uomExists = inventory.uom[unit] != null;
        return {
          product,
          targetState,
          unit,
          uomExists,
          stateDiffers: product.state !== targetState,
          costDiffers: excelCost != null && uomExists && excelCost !== product.cost,
        };
      });

    // 하나라도 다르면 형제 unit까지 모두 노출해 담당자가 unit별로 판단·직접입력하게 한다.
    if (!evaluated.some((e) => e.stateDiffers || e.costDiffers)) return;

    // 개별/박스 등 서로 다른 단위가 2개 이상이면 복합 단위 재고로 표시한다.
    const multiUnit = new Set(evaluated.map((e) => e.unit)).size > 1;

    evaluated.forEach(({ product, targetState, unit, uomExists }) => {
      const row: DiffRow = {
        productId: product._id,
        code,
        inventoryName: inventory.inventoryName,
        fullName: product.fullName,
        currentState: product.state,
        targetState,
        inventoryId: inventory._id,
        inventoryUnit: unit,
        currentCost: product.cost,
        // 엑셀 공급단가(코드당 1개)를 그대로 노출. bundle 등은 담당자가 직접입력으로 보정.
        excelCost: uomExists ? excelCost : null,
        overrideCost: null,
        multiUnit,
      };
      // ponytail: 판매중지(discontinued)는 자동 반영으로 되살리지 않도록 제외하고 별도 집계
      if (product.state === ProductStateCode.DISCONTINUED) {
        discontinued.push(row);
      } else {
        diffs.push(row);
      }
    });
  });

  return { diffs, discontinued };
};

/**
 * 우주식품(fugBvrUFgCg2fOJOMeAN) 품절여부 처리 모달
 * 엑셀 업로드 → 미리보기 → 동기화(서버 비교) → 선택 반영.
 */
const WoojooSoldOutSyncModal: FC = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileToUpload, setFileToUpload] = useState<File>();
  const [excelRows, setExcelRows] = useState<WoojooExcelRow[]>();
  const [diffs, setDiffs] = useState<DiffRow[]>();
  const [discontinued, setDiscontinued] = useState<DiffRow[]>([]);
  const [selectedRows, setSelectedRows] = useState<DiffRow[]>([]);
  const gridApiRef = useRef<GridApi<DiffRow>>();

  const reset = () => {
    setFileToUpload(undefined);
    setExcelRows(undefined);
    setDiffs(undefined);
    setDiscontinued([]);
    setSelectedRows([]);
  };

  const closeModal = () => {
    if (loading) return;
    reset();
    setOpen(false);
  };

  // 파일 업로드 시 엑셀을 파싱해 미리보기 데이터로 만든다.
  useEffect(() => {
    if (!fileToUpload) return;
    setLoading(true);
    parseExcel(fileToUpload)
      .then((rows) => {
        if (!rows.length || !('상품코드' in rows[0]) || !('품절여부' in rows[0])) {
          throw new Error("'상품코드', '품절여부' 컬럼이 있는 엑셀 파일이 아닙니다.");
        }
        setExcelRows(rows);
      })
      .catch((error) => {
        notification.error({ message: '파일 업로드 실패', description: errorObjectToString(error) });
        reset();
      })
      .finally(() => setLoading(false));
  }, [fileToUpload]);

  const onSync = async () => {
    if (!excelRows) return;
    setLoading(true);
    try {
      const result = await computeDiffs(excelRows);
      setDiffs(result.diffs);
      setDiscontinued(result.discontinued);
    } catch (error) {
      notification.error({ message: '동기화 실패', description: errorObjectToString(error) });
    } finally {
      setLoading(false);
    }
  };

  const onApply = async () => {
    // 직접 입력 공급가 편집을 반영하기 위해 그리드에서 최신 선택 행을 읽는다.
    const rows = gridApiRef.current?.getSelectedRows() ?? selectedRows;
    if (!rows.length) {
      notification.warning({ message: '반영할 상품을 선택해주세요.' });
      return;
    }
    setLoading(true);
    try {
      const changes: FieldChange[] = [];
      const productIds = new Set<string>();
      const inventoryIds = new Set<string>();

      // 1) 상태(품절여부)는 상품에 직접 반영한다. (batch)
      // ponytail: 단일 batch — firestore 500 write 제한. 선택 상품이 그 이상이면 나눠서 반영.
      let stateCount = 0;
      firebaseManager.batchStart();
      for (const row of rows) {
        if (row.currentState === row.targetState) continue;
        await updateProduct(row.productId, { state: row.targetState }, { bBatch: true, audit: { suppress: true } });
        changes.push({ field: 'state', before: row.currentState, after: row.targetState });
        productIds.add(row.productId);
        stateCount += 1;
      }
      await firebaseManager.batchEnd();

      // 2) 공급가는 매입가 정본인 재고 uom[단위].cost에 반영한다.
      //    onUpdateInventory 트리거가 연결된 상품의 cost로 캐스케이드한다.
      //    (재고,단위)로 dedupe — 같은 키에 여러 행이면 마지막 값 적용.
      const costWrites = new Map<string, { inventoryId: string; unit: ProductInventoryUnit; cost: number }>();
      for (const row of rows) {
        const finalCost = row.overrideCost != null ? row.overrideCost : row.excelCost;
        if (finalCost == null || finalCost === row.currentCost) continue;
        costWrites.set(`${row.inventoryId}|${row.inventoryUnit}`, {
          inventoryId: row.inventoryId,
          unit: row.inventoryUnit,
          cost: finalCost,
        });
        changes.push({ field: `uom.${row.inventoryUnit}.cost`, before: row.currentCost, after: finalCost });
        productIds.add(row.productId);
        inventoryIds.add(row.inventoryId);
      }
      for (const { inventoryId, unit, cost } of costWrites.values()) {
        await updateInventoryUnitCost(inventoryId, unit, cost, { audit: { suppress: true } });
      }

      if (stateCount === 0 && costWrites.size === 0) {
        notification.info({ message: '반영할 변경사항이 없습니다.' });
        setLoading(false);
        return;
      }

      // 일괄 변경을 감사로그 1건으로 요약 기록한다.
      auditLogger.log({
        action: 'BULK_UPDATE',
        entity: 'product',
        entityId: `woojoo-sold-out-sync-${productIds.size}`,
        summary: `우주식품 동기화 — 상태 ${stateCount}건, 공급가 ${costWrites.size}건(재고 단위)`,
        changes,
        metadata: {
          supplierId: SUPPLIER_ID,
          fileName: fileToUpload?.name,
          productIds: [...productIds],
          inventoryIds: [...inventoryIds],
        },
      });
      notification.success({
        message: `상태 ${stateCount}건, 공급가 ${costWrites.size}건(재고 단위)을 반영했습니다.`,
        description: costWrites.size > 0 ? '공급가는 재고에 반영되어 연결된 상품에 자동 동기화됩니다.' : undefined,
      });
      closeModal();
    } catch (error) {
      notification.error({ message: '반영 실패', description: errorObjectToString(error) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button size='small' type='primary' style={{ fontSize: 12 }} onClick={() => setOpen(true)}>
        재고 동기화
      </Button>
      <Modal
        title='우주식품 재고 동기화'
        open={open}
        onCancel={closeModal}
        width='1280px'
        maskClosable={false}
        keyboard={false}
        centered
        footer={null}
        styles={{ body: { height: 'calc(100vh - 220px)', display: 'flex', flexDirection: 'column' } }}
      >
        {/* 1. 파일 없음 → 업로드 */}
        {!fileToUpload && (
          <FileUploadArea
            fileToUpload={fileToUpload}
            setFile={setFileToUpload}
            subTitle='우주식품 공급가 엑셀파일만 지원합니다.'
          />
        )}

        {/* 2. 파일 있음 */}
        {fileToUpload && (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <span style={{ fontWeight: 600 }}>
                {fileToUpload.name}
                {excelRows && (
                  <span style={{ marginLeft: 8, fontWeight: 400, color: '#888' }}>총 {excelRows.length}행</span>
                )}
              </span>
              <Button type='text' icon={<DeleteOutlined />} danger onClick={reset} disabled={loading} />
            </div>

            {diffs !== undefined && discontinued.length > 0 && (
              <Popover
                trigger='click'
                placement='bottomLeft'
                title={`제외된 판매중지 상품 ${discontinued.length}개`}
                content={
                  <div style={{ maxHeight: 320, overflow: 'auto', maxWidth: 480 }}>
                    {discontinued.map((row) => (
                      <div key={row.productId} style={{ padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <div>{row.fullName}</div>
                        <div style={{ fontSize: 12, color: '#888' }}>
                          {row.code} · {row.inventoryName} · 엑셀: {productStates[row.targetState]}
                        </div>
                      </div>
                    ))}
                  </div>
                }
              >
                <Alert
                  type='warning'
                  showIcon
                  style={{ marginBottom: 8, cursor: 'pointer' }}
                  message={`판매중지 상품 ${discontinued.length}개는 엑셀 기준 반영 대상이지만 되살림 방지를 위해 동기화 대상에서 제외했습니다. (클릭하여 목록 보기)`}
                />
              </Popover>
            )}

            <div className='ag-theme-alpine' style={{ flex: 1, minHeight: 0 }}>
              {diffs === undefined ? (
                <AgGridReact<WoojooExcelRow> rowData={excelRows} columnDefs={previewColDefs} enableCellTextSelection />
              ) : (
                <AgGridReact<DiffRow>
                  rowData={diffs}
                  columnDefs={diffColDefs}
                  defaultColDef={{ sortable: true, resizable: true, filter: true }}
                  rowSelection='multiple'
                  suppressRowClickSelection
                  stopEditingWhenCellsLoseFocus
                  enableCellTextSelection
                  getRowStyle={(params) => (params.data?.multiUnit ? { backgroundColor: '#f9f0ff' } : undefined)}
                  onGridReady={(e: GridReadyEvent<DiffRow>) => (gridApiRef.current = e.api)}
                  onSelectionChanged={(e: SelectionChangedEvent<DiffRow>) => setSelectedRows(e.api.getSelectedRows())}
                />
              )}
            </div>

            {/* 액션 */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              {diffs === undefined ? (
                <Button type='primary' onClick={onSync} loading={loading} disabled={!excelRows}>
                  동기화
                </Button>
              ) : (
                <>
                  <Button onClick={() => setDiffs(undefined)} disabled={loading}>
                    다시 동기화
                  </Button>
                  <Button type='primary' onClick={onApply} loading={loading} disabled={selectedRows.length === 0}>
                    {selectedRows.length}개 반영하기
                  </Button>
                </>
              )}
            </div>

            {loading && diffs === undefined && excelRows === undefined && (
              <LoadingOutlined style={{ fontSize: 24, alignSelf: 'center', marginTop: 16 }} />
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default WoojooSoldOutSyncModal;
